import { useCallback, useMemo } from 'react';
import {
  useQuery,
  useQueries,
  useQueryClient,
  type Query,
  type QueryClient,
} from '@tanstack/react-query';

import { canvasSnapshotApi, type CanvasSnapshotQuery } from '@/services/api/canvas-snapshot';
import type { GenerationTargetKey } from '@/types/generation-reference';
import type { CanvasSnapshot, CanvasTargetHistory } from '@/types/canvas-snapshot';
import {
  buildGenerationTargetCacheKey,
  buildCanvasHistoryQueryCacheKey,
} from '@/lib/generation/generation-reference';

export const canvasSnapshotKeys = {
  all: ['canvas-snapshot'] as const,
  context: (params: CanvasSnapshotQuery) =>
    [
      ...canvasSnapshotKeys.all,
      params.projectId,
      params.contextType,
      params.assetType ?? null,
      params.assetId ?? null,
      params.storyboardId ?? null,
    ] as const,
  history: (params: CanvasSnapshotQuery, target: GenerationTargetKey, resourceId?: string | null) =>
    [
      ...canvasSnapshotKeys.context(params),
      'history',
      buildGenerationTargetCacheKey(target),
      resourceId ?? null,
    ] as const,
};

/** history 查询挂在同一前缀下，写 snapshot 缓存时必须排除。 */
export function isCanvasSnapshotCacheQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'canvas-snapshot' && !queryKey.includes('history');
}

export function isCanvasSnapshotCacheData(data: unknown): data is CanvasSnapshot {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as Partial<CanvasSnapshot>;
  return (
    typeof candidate.projectId === 'string' &&
    Array.isArray(candidate.drafts) &&
    Array.isArray(candidate.targets)
  );
}

export function createCanvasSnapshotQueryFilter(projectId?: string) {
  return {
    queryKey: projectId
      ? ([...canvasSnapshotKeys.all, projectId] as const)
      : canvasSnapshotKeys.all,
    predicate: (query: Query) => isCanvasSnapshotCacheQuery(query.queryKey),
  };
}

/** 从画布 history 缓存中立刻去掉指定 resource（删除后避免残影叶子/黑框）。 */
export function removeResourceFromCanvasHistoryCaches(
  queryClient: QueryClient,
  resourceId: string,
  options?: { projectId?: string; storyboardId?: string; assetId?: string }
): void {
  const trimmedId = resourceId.trim();
  if (!trimmedId) return;

  queryClient.setQueriesData<CanvasTargetHistory>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        if (key[0] !== 'canvas-snapshot' || !key.includes('history')) return false;
        if (options?.projectId && key[1] !== options.projectId) return false;
        if (options?.storyboardId && !key.includes(options.storyboardId)) return false;
        if (options?.assetId && !key.includes(options.assetId)) return false;
        return true;
      },
    },
    (current) => {
      if (!current || !Array.isArray(current.items)) return current;
      const nextItems = current.items.filter((item) => item.resource.resourceId !== trimmedId);
      if (nextItems.length === current.items.length) return current;
      const removed = current.items.length - nextItems.length;
      return {
        ...current,
        items: nextItems,
        total: Math.max(0, (current.total ?? current.items.length) - removed),
      };
    }
  );
}

/** 失效某分镜下所有画布 history 查询（编辑页增删后驱动画布组重排）。 */
export function invalidateCanvasHistoryQueriesForStoryboard(
  queryClient: QueryClient,
  storyboardId: string,
  projectId?: string,
  options?: { includeSnapshot?: boolean }
): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (key[0] !== 'canvas-snapshot') return false;
      if (!key.includes('history')) return false;
      if (projectId && key[1] !== projectId) return false;
      return key.includes(storyboardId);
    },
  });
  // 编辑入库只需刷 history；连带失效 snapshot 会触发更大范围重同步，易打乱已展开网格。
  if (options?.includeSnapshot === false) return;
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!isCanvasSnapshotCacheQuery(key)) return false;
      if (projectId && key[1] !== projectId) return false;
      return key.includes(storyboardId);
    },
  });
}

/** 组件层封装：画布生成完成后失效 history / snapshot。 */
export function useInvalidateCanvasHistoryQueriesForStoryboard() {
  const queryClient = useQueryClient();

  return useCallback(
    (storyboardId: string, projectId?: string, options?: { includeSnapshot?: boolean }) => {
      invalidateCanvasHistoryQueriesForStoryboard(queryClient, storyboardId, projectId, options);
    },
    [queryClient]
  );
}

/** 失效某资产画布下所有 history 查询（标签生成完成后驱动已展开历史组更新）。 */
export function invalidateCanvasHistoryQueriesForAsset(
  queryClient: QueryClient,
  params: {
    projectId: string;
    assetId: string;
    assetType?: string;
    includeSnapshot?: boolean;
  }
): void {
  const { projectId, assetId, assetType, includeSnapshot = true } = params;
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (key[0] !== 'canvas-snapshot') return false;
      if (!key.includes('history')) return false;
      if (key[1] !== projectId) return false;
      if (assetType && !key.includes(assetType)) return false;
      return key.includes(assetId);
    },
  });
  if (!includeSnapshot) return;
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!isCanvasSnapshotCacheQuery(key)) return false;
      if (key[1] !== projectId) return false;
      if (assetType && !key.includes(assetType)) return false;
      return key.includes(assetId);
    },
  });
}

/** 组件层封装：资产标签生成完成后失效 history / snapshot。 */
export function useInvalidateCanvasHistoryQueriesForAsset() {
  const queryClient = useQueryClient();

  return useCallback(
    (params: {
      projectId: string;
      assetId: string;
      assetType?: string;
      includeSnapshot?: boolean;
    }) => {
      invalidateCanvasHistoryQueriesForAsset(queryClient, params);
    },
    [queryClient]
  );
}

export function removeAttachmentFromCanvasSnapshotCache(
  queryClient: QueryClient,
  projectId: string,
  attachmentId: string
) {
  queryClient.setQueriesData<CanvasSnapshot>(
    createCanvasSnapshotQueryFilter(projectId),
    (snapshot) =>
      snapshot && isCanvasSnapshotCacheData(snapshot)
        ? {
            ...snapshot,
            attachments: snapshot.attachments.filter(
              (attachment) => attachment.attachmentId !== attachmentId
            ),
            referenceSources: snapshot.referenceSources.filter(
              (source) => !(source.resourceType === 4 && source.resourceId === attachmentId)
            ),
            drafts: snapshot.drafts.map((draft) => ({
              ...draft,
              references: draft.references.filter(
                (reference) =>
                  !(reference.resourceType === 4 && reference.resourceId === attachmentId)
              ),
            })),
          }
        : snapshot
  );
}

export function useRemoveAttachmentFromCanvasSnapshotCache(projectId: string) {
  const queryClient = useQueryClient();

  return useCallback(
    (attachmentId: string) => {
      removeAttachmentFromCanvasSnapshotCache(queryClient, projectId, attachmentId);
    },
    [projectId, queryClient]
  );
}

export function useCanvasSnapshot(
  params: CanvasSnapshotQuery & {
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: canvasSnapshotKeys.context(params),
    queryFn: () => canvasSnapshotApi.getSnapshot(params),
    enabled: (params.enabled ?? true) && Boolean(params.projectId),
    // 每次进入画布都拉当前 context 节点，避免从资产改定稿回到分镜仍见旧封面。
    refetchOnMount: 'always',
  });
}

export function useCanvasTargetHistory(
  params: CanvasSnapshotQuery & {
    target: GenerationTargetKey | null;
    resourceId?: string | null;
    page?: number;
    pageSize?: number;
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: params.target
      ? [
          ...canvasSnapshotKeys.history(params, params.target, params.resourceId),
          params.page ?? 1,
          params.pageSize ?? 50,
        ]
      : [...canvasSnapshotKeys.context(params), 'history', 'disabled'],
    queryFn: () => {
      if (!params.target) throw new Error('Missing generation target');
      return canvasSnapshotApi.getHistory({
        ...params,
        target: params.target,
        resourceId: params.resourceId,
        page: params.page,
        pageSize: params.pageSize,
      });
    },
    enabled: (params.enabled ?? false) && Boolean(params.projectId) && Boolean(params.target),
  });
}

/** 画布 history 查询项：同 plan target 可挂不同 resourceId（如多视频 versions 链）。 */
export type CanvasHistoryQueryItem = {
  target: GenerationTargetKey;
  resourceId?: string | null;
};

/** 并行拉取多个展开 target 的 history，供多开历史版本组使用。 */
export function useCanvasTargetHistories(
  params: CanvasSnapshotQuery & {
    /** 优先：允许同 planId + 不同 resourceId 各发一次 history。 */
    historyQueries?: readonly CanvasHistoryQueryItem[];
    /** 兼容：无 resourceId 的 target 列表（图片 / 资产标签）。 */
    targets?: readonly GenerationTargetKey[];
    /** @deprecated 同 plan 会后写覆盖；请改用 historyQueries。 */
    resourceIdByTargetKey?: ReadonlyMap<string, string> | null;
    page?: number;
    pageSize?: number;
    enabled?: boolean;
  }
) {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 50;
  const resourceIdByTargetKey = params.resourceIdByTargetKey ?? null;

  const historyQueries = useMemo<CanvasHistoryQueryItem[]>(() => {
    if (params.historyQueries) return [...params.historyQueries];
    return (params.targets ?? []).map((target) => ({
      target,
      resourceId: resourceIdByTargetKey?.get(buildGenerationTargetCacheKey(target)) ?? null,
    }));
  }, [params.historyQueries, params.targets, resourceIdByTargetKey]);

  const enabled =
    (params.enabled ?? true) && Boolean(params.projectId) && historyQueries.length > 0;

  const queries = useQueries({
    queries: historyQueries.map((item) => {
      const resourceId = item.resourceId ?? null;
      return {
        queryKey: [
          ...canvasSnapshotKeys.history(params, item.target, resourceId),
          page,
          pageSize,
        ] as const,
        queryFn: () =>
          canvasSnapshotApi.getHistory({
            ...params,
            target: item.target,
            resourceId,
            page,
            pageSize,
          }),
        enabled,
      };
    }),
  });

  const historiesByQueryKey = useMemo(() => {
    const map = new Map<string, (typeof queries)[number]['data']>();
    historyQueries.forEach((item, index) => {
      const data = queries[index]?.data;
      if (!data) return;
      map.set(buildCanvasHistoryQueryCacheKey(item.target, item.resourceId), data);
    });
    return map;
  }, [historyQueries, queries]);

  /** 无 resourceId 的查询（图片 / 资产）按 targetKey 索引；同 key 后写覆盖。 */
  const historiesByTargetKey = useMemo(() => {
    const map = new Map<string, (typeof queries)[number]['data']>();
    historyQueries.forEach((item, index) => {
      if (item.resourceId) return;
      const data = queries[index]?.data;
      if (data) {
        map.set(buildGenerationTargetCacheKey(item.target), data);
      }
    });
    return map;
  }, [historyQueries, queries]);

  /** 有 resourceId 时按 videoId / resourceId 索引，同 plan 多视频互不覆盖。 */
  const historiesByResourceId = useMemo(() => {
    const map = new Map<string, NonNullable<(typeof queries)[number]['data']>>();
    historyQueries.forEach((item, index) => {
      const resourceId = item.resourceId?.trim();
      if (!resourceId) return;
      const data = queries[index]?.data;
      if (data) map.set(resourceId, data);
    });
    return map;
  }, [historyQueries, queries]);

  const isLoading = queries.some((query) => query.isLoading);
  const isFetching = queries.some((query) => query.isFetching);
  const loadingTargetKeys = useMemo(() => {
    const keys = new Set<string>();
    historyQueries.forEach((item, index) => {
      const query = queries[index];
      if (query?.isLoading || query?.isFetching) {
        keys.add(buildGenerationTargetCacheKey(item.target));
      }
    });
    return keys;
  }, [historyQueries, queries]);

  const loadingResourceIds = useMemo(() => {
    const keys = new Set<string>();
    historyQueries.forEach((item, index) => {
      const resourceId = item.resourceId?.trim();
      if (!resourceId) return;
      const query = queries[index];
      if (query?.isLoading || query?.isFetching) keys.add(resourceId);
    });
    return keys;
  }, [historyQueries, queries]);

  return {
    historyQueries,
    queries,
    historiesByQueryKey,
    historiesByTargetKey,
    historiesByResourceId,
    isLoading,
    isFetching,
    loadingTargetKeys,
    loadingResourceIds,
    refetchAll: () => Promise.all(queries.map((query) => query.refetch())),
  };
}
